import {useContext, useMemo} from 'react';
import {CssBaseline} from '@material-ui/core';
import {
    createTheme,
    responsiveFontSizes,
    ThemeProvider,
} from '@material-ui/core/styles';
import {AppCtx} from 'context/AppContext';

const OurThemeProvider = ({children}) => {
    const {darkMode} = useContext(AppCtx);

    const theme = useMemo(() => {
        let ourTheme = createTheme({
            palette: {
                type: darkMode ? 'dark' : 'light',
                primary: {
                    main: darkMode ? '#2083ee' : '#0B5D98',
                    light: darkMode ? '#4784B0' : '#4784B0',
                    light2: darkMode ? '#ACC4D6' : '#ACC4D6',
                    light3: darkMode ? '#ACC4D6' : '#ACC4D6',
                    light4: darkMode ? '#D3E1EC' : '#D3E1EC',
                    white: '#fff',
                },
                secondary: {
                    main: '#EE4000',
                    light: darkMode ? '#F9C0AB' : '#F9C0AB',
                },
                success: {
                    main: darkMode ? '#99FFCC' : '#81c784',
                },
                gray: {
                    light: darkMode ? '#636363' : '#F5F5F5',
                    light2: darkMode ? '#454545' : '#E5E5E5',
                    medium: darkMode ? '#151515' : '#979797',
                },
                charts: {
                    oceanBlue: darkMode ? '#0B5D98' : '#0B5D98',
                    nearCoastalBlue: darkMode ? '#4784B0' : '#4784B0',
                    inandBlue: darkMode ? '#7DA6C5' : '#7DA6C5',
                    pearlWhite: darkMode ? '#11507D' : '#11507D',
                    green: darkMode ? '#27AE60' : '#27AE60',
                    lightOrange: darkMode ? '#F2994A' : '#F2994A',
                    lightRed: darkMode ? '#EB5757' : '#EB5757',
                },
            },
            overrides: {
                MuiFormHelperText: {
                    root: {
                        fontSize: '1rem',
                    },
                },
                MuiCssBaseline: {
                    '@global': {
                        body: {
                            scrollbarColor: darkMode
                                ? '#6b6b6b #2b2b2b'
                                : '#C1C1C1 #FAFAFA',
                            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                                backgroundColor: darkMode
                                    ? '#2b2b2b'
                                    : '#FAFAFA',
                            },
                            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb':
                                {
                                    borderRadius: 8,
                                    backgroundColor: darkMode
                                        ? '#6b6b6b'
                                        : '#C1C1C1',
                                    minHeight: 24,
                                    border: darkMode
                                        ? '3px solid #2b2b2b'
                                        : '3px solid #FAFAFA',
                                },
                            '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus':
                                {
                                    backgroundColor: darkMode
                                        ? '#959595'
                                        : '#959595',
                                },
                            '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active':
                                {
                                    backgroundColor: darkMode
                                        ? '#959595'
                                        : '#959595',
                                },
                            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover':
                                {
                                    backgroundColor: darkMode
                                        ? '#959595'
                                        : '#959595',
                                },
                            '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner':
                                {
                                    backgroundColor: darkMode
                                        ? '#2b2b2b'
                                        : '#FAFAFA',
                                },
                        },
                    },
                },
            },
            breakpoints: {
                values: {
                    xs: 0,
                    xss: 300,
                    sm: 600,
                    smm: 750,
                    md: 960,
                    mdd: 1075,
                    lg: 1280,
                    lgg: 1470,
                    xl: 1920,
                },
            },
        });
        ourTheme = responsiveFontSizes(ourTheme);

        ourTheme.typography.subtitle1 = {
            fontSize: '.8rem',
            fontWeight: 'normal',
            [ourTheme.breakpoints.up('sm')]: {
                fontSize: '1rem',
            },
        };

        ourTheme.typography.h6 = {
            ...ourTheme.typography.h6,
            [ourTheme.breakpoints.up('md')]: {
                fontSize: '1.5rem',
            },
            [ourTheme.breakpoints.up('lg')]: {
                fontSize: '1.5rem',
            },
        };

        if (process?.env.NODE_ENV !== 'production' && window) {
            window.theme = ourTheme;
        }
        return ourTheme;
    }, [darkMode]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {children}
        </ThemeProvider>
    );
};

export default OurThemeProvider;
